import { Box, Button, Typography } from '@mui/material'
import React, { ReactNode } from 'react'
import { Link } from 'react-router-dom'

export type RecoveryIllustrationVariant = 'notFound' | 'error'

interface RecoveryPageProps {
  eyebrow: string
  title: string
  description?: string
  illustration: ReactNode
  actions?: ReactNode
  embedded?: boolean
}

export const RecoveryPage: React.FC<RecoveryPageProps> = ({
  eyebrow,
  title,
  description,
  illustration,
  actions,
  embedded = false,
}) => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: embedded ? 'auto' : '100vh',
      backgroundColor: embedded ? 'transparent' : theme.palette.background.default,
      color: theme.palette.text.primary,
      px: embedded ? { xs: 2, sm: 3 } : { xs: 3, sm: 6 },
      py: embedded ? { xs: 3, sm: 4 } : { xs: 6, sm: 10 },
      textAlign: 'center',
    })}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: embedded ? '36rem' : '48rem',
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: '100%',
          maxWidth: embedded ? { xs: '18rem', sm: '22rem' } : { xs: '26rem', sm: '36rem' },
          mb: embedded ? { xs: 2, sm: 3 } : { xs: 4, sm: 6 },
          '& svg': {
            display: 'block',
            width: '100%',
            height: 'auto',
          },
        }}
      >
        {illustration}
      </Box>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: embedded ? '0.25em' : '0.35em',
          fontWeight: 700,
          fontSize: embedded ? '1.1rem' : '1.3rem',
          color: 'primary.main',
          mb: 1.5,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        sx={{
          fontWeight: 700,
          maxWidth: '40rem',
          fontSize: embedded ? { xs: '1.8rem', sm: '2.2rem' } : { xs: '2.4rem', sm: '3rem' },
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {description ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: '42rem',
            mb: actions ? 5 : 0,
            fontSize: embedded ? { xs: '1.3rem', sm: '1.5rem' } : { xs: '1.5rem', sm: '1.7rem' },
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      ) : null}
      {actions ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: description ? 0 : 3 }}>
          {actions}
        </Box>
      ) : null}
    </Box>
  </Box>
)

interface RecoveryPageButtonProps {
  label: string
  onClick?: () => void
  to?: string
}

export const RecoveryPageButton: React.FC<RecoveryPageButtonProps> = ({ label, onClick, to }) => {
  if (to) {
    return (
      <Button variant="contained" color="primary" size="large" component={Link} to={to}>
        {label}
      </Button>
    )
  }

  return (
    <Button variant="contained" color="primary" size="large" onClick={onClick}>
      {label}
    </Button>
  )
}
